import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useState } from "react";
import { Home, Plus, Briefcase, Settings, Building2, Database, ListOrdered, BarChart3, LogOut, HelpCircle, Mail, MessageSquare, Gauge, UploadCloud, ClipboardCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import WelcomeOnboarding from "@/components/WelcomeOnboarding";
import HelpDrawer from "@/components/HelpDrawer";
import NotificationBell from "@/components/NotificationBell";
import GetHelpButton from "@/components/GetHelpButton";

const clientNav = [
  { label: "Role Board", icon: Home, path: "/" },
  { label: "New Role Search", icon: Plus, path: "/search/new" },
  { label: "Work Orders", icon: Briefcase, path: "/work-orders" },
  { label: "Messages", icon: Mail, path: "/messages" },
  { label: "Settings", icon: Settings, path: "/settings" },
  { label: "Help", icon: HelpCircle, path: "/help" },
];

const adminNav = [
  { label: "Client Management", icon: Building2, path: "/admin/clients" },
  { label: "Demo Funnel", icon: Gauge, path: "/admin/demo-funnel" },
  { label: "Candidate Database", icon: Database, path: "/admin/candidates" },
  { label: "Candidate Upload", icon: UploadCloud, path: "/admin/candidate-upload" },
  { label: "Candidate Review", icon: ClipboardCheck, path: "/admin/candidate-review" },
  { label: "Work Order Queue", icon: ListOrdered, path: "/admin/work-orders" },
  { label: "Communications", icon: MessageSquare, path: "/admin/communications" },
  { label: "Platform Analytics", icon: BarChart3, path: "/admin/analytics" },
];

export default function AppSidebar() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const initials = user?.full_name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";

  return (
    <div className="flex flex-col h-screen md:flex-row" data-testid="app-layout">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-shrink-0 bg-gradient-to-br from-evergreen-950 via-evergreen-900 to-evergreen-950 text-white flex-col" data-testid="app-sidebar">
        <div className="p-5 pb-4">
          <div className="flex items-center gap-2">
            <img src="/varyynt-icon.svg" alt="" className="h-8 w-8" />
            <div>
              <span className="text-base font-heading text-white tracking-tight">Varyynt Intel</span>
              <p className="text-[10px] text-slate-400 font-accent tracking-wide">Talent Intelligence Platform</p>
            </div>
          </div>
        </div>
        <Separator className="bg-white/10" />
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {clientNav.map((item) => (
            <button
              key={item.path}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                isActive(item.path)
                  ? "text-lime-700 bg-lime-50"
                  : "text-slate-300 hover:text-white hover:bg-white/10"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </button>
          ))}
          {isAdmin && (
            <>
              <div className="pt-4 pb-2 px-4">
                <span className="text-xs font-mono uppercase tracking-widest text-slate-400">Admin</span>
              </div>
              {adminNav.map((item) => (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive(item.path)
                      ? "text-lime-700 bg-lime-50"
                      : "text-slate-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </button>
              ))}
            </>
          )}
        </nav>
        <Separator className="bg-white/10" />
        <div className="p-4">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-lime-600 text-white text-xs font-accent">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <Badge variant="outline" className="text-[10px] border-white/20 text-slate-300 font-mono">
                {user?.role === "master_admin" ? "Admin" : user?.role === "client_admin" ? "Org Admin" : "Member"}
              </Badge>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="logout-btn"
            onClick={logout}
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/10"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 to-slate-100 pb-16 md:pb-0">
        {/* Demo Mode Banner */}
        {user?.org_demo_mode && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-center gap-2" data-testid="demo-banner">
            <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <p className="text-sm text-amber-800 font-accent">
              You're exploring Varyynt Intel in demo mode. <span className="font-semibold">Contact your Varyynt representative to activate full access.</span>
            </p>
          </div>
        )}
        {/* Pool Change Alerts */}
        {user?.pool_alerts?.length > 0 && (
          <div className="px-4 md:px-8 pt-3 space-y-2" data-testid="pool-alerts">
            {user.pool_alerts.map((a, i) => (
              <div key={i} className="bg-teal-50 border border-teal-200 rounded-lg px-4 py-2.5 flex items-center justify-between animate-slideDown" data-testid={`pool-alert-${a.search_id}`}>
                <p className="text-sm text-teal-800">
                  <span className="font-semibold">{a.search_title}</span>: pool changed from {a.previous_count} to <span className="font-bold">{a.current_count}</span>
                  <span className={`ml-1 font-accent font-semibold ${a.change > 0 ? "text-lime-700" : "text-red-600"}`}>({a.change > 0 ? "+" : ""}{a.change})</span>
                </p>
                <button onClick={() => navigate(`/search/${a.search_id}`)} className="text-xs font-accent text-teal-700 hover:text-teal-900 font-medium whitespace-nowrap ml-3">View Search</button>
              </div>
            ))}
          </div>
        )}
        {/* Contextual help button */}
        <div className="flex items-center justify-end gap-2 px-4 pt-3 md:px-8 md:pt-4">
          <NotificationBell />
          <HelpDrawer pageKey={
            location.pathname === "/" ? "/" :
            location.pathname.startsWith("/search") ? "/search" :
            location.pathname.startsWith("/work-orders") ? "/work-orders" :
            location.pathname.startsWith("/settings") ? "/settings" :
            location.pathname.startsWith("/admin/clients") ? "/admin/clients" :
            location.pathname.startsWith("/admin/work-orders") ? "/admin/work-orders" :
            location.pathname.startsWith("/admin/candidates") ? "/admin/candidates" :
            location.pathname.startsWith("/admin/analytics") ? "/admin/analytics" :
            "/"
          } />
        </div>
        <Outlet />
      </main>

      {/* Welcome Onboarding */}
      <WelcomeOnboarding />

      {/* Get Help floating button */}
      <GetHelpButton />

      {/* Mobile Bottom Tab Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 safe-area-bottom" data-testid="mobile-tab-bar">
        <div className="grid grid-cols-5 w-full">
          {clientNav.filter(i => i.path !== "/help").map((item) => (
            <button
              key={item.path}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => navigate(item.path)}
              className={`flex flex-col items-center justify-center gap-0.5 py-2 transition-colors ${
                isActive(item.path)
                  ? "text-lime-700"
                  : "text-slate-400"
              }`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span className="text-[10px] font-accent font-medium leading-tight text-center">
                {item.label === "New Role Search" ? "Search" : item.label === "Work Orders" ? "Orders" : item.label}
              </span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}
